<template>
  <div class="smart-matching">
    <div v-if="loading" class="text-center p-8">
      <vs-progress indeterminate color="primary"></vs-progress>
      <p class="mt-4">Analysing…</p>
    </div>

    <div v-else-if="!byTransaction.length" class="text-center p-8">
      <h4>No unallocated transaction found.</h4>
    </div>

    <div v-else>
      <div class="mb-4 flex items-center justify-between">
        <h4>{{ byTransaction.length }} transaction(s) to reconcile</h4>
        <div>
          <vs-chip color="success" class="mr-2">&gt;=80 reliable</vs-chip>
          <vs-chip color="warning" class="mr-2">40-79 to review</vs-chip>
          <vs-chip color="primary" class="mr-2">Grouped</vs-chip>
          <vs-chip color="warning">Oldest first</vs-chip>
        </div>
      </div>

      <div v-for="row in byTransaction" :key="row.transaction.id" class="match-row mb-4">
        <div class="grid grid-cols-12 gap-4">
          <!-- Transaction column -->
          <div class="col-span-5 tx-cell">
            <div class="font-bold text-lg">
              {{ formatAmount(row.transaction.gcl_amount) }}
              {{ row.transaction.gcl_currency || 'EUR' }}
            </div>
            <div v-if="parseAmount(row.transaction.gcl_amount_allocated) > 0.01" class="text-xs text-grey">
              left to place — {{ formatAmount(row.transaction.gcl_amount_received) }} received,
              {{ formatAmount(row.transaction.gcl_amount_allocated) }} already allocated
            </div>
            <div class="text-sm">{{ formatDate(row.transaction.gcl_valuedate) }}</div>
            <div class="font-semibold mt-1">{{ row.transaction.gcl_debtorname || '—' }}</div>
            <div class="text-xs text-grey mt-1" :title="row.transaction.gcl_comment">
              {{ truncate(row.transaction.gcl_comment, 120) }}
            </div>
            <div class="text-xs mt-1 text-grey">{{ row.transaction.gcl_iban }}</div>
          </div>

          <!-- Candidates column -->
          <div class="col-span-7 candidates-cell">
            <div v-if="!row.candidates.length && !row.groupedSuggestion" class="text-grey p-2">
              No candidate scoring 40 or above.
            </div>

            <!-- Single-invoice candidates -->
            <div v-for="cand in row.candidates" :key="cand.invoiceId" class="cand-card">
              <div class="flex items-center justify-between">
                <div>
                  <vs-chip :color="confidenceColor(cand.score)" size="small">{{ cand.score }}</vs-chip>
                  <span class="font-bold ml-2">{{ cand.invoice_no }}</span>
                  <span class="ml-2">{{ cand.company_name }}</span>
                </div>
                <div class="text-right">
                  <div class="font-semibold">{{ formatAmount(cand.remaining_due) }}</div>
                  <div class="text-xs text-grey">outstanding</div>
                </div>
              </div>
              <div class="text-xs text-grey mt-1">
                <span v-for="r in cand.reasons" :key="r" class="reason-tag">{{ r }}</span>
              </div>
              <div class="mt-2 flex gap-2">
                <vs-button size="small" color="success" @click="allocateFull(row.transaction, cand)">
                  {{ allocationLabel(row.transaction, cand) }}
                </vs-button>
                <vs-button size="small" type="border" @click="openSplit(row.transaction, cand)">Split…</vs-button>
              </div>
            </div>

            <!-- Grouped suggestion. 'oldest_first' = the payer is known but no
                 combination of invoices adds up: oldest invoices first, to review. -->
            <div v-if="row.groupedSuggestion" class="cand-card grouped"
              :class="{ 'oldest-first': row.groupedSuggestion.kind === 'oldest_first' }">
              <div class="flex items-center justify-between">
                <div>
                  <vs-chip :color="row.groupedSuggestion.kind === 'oldest_first' ? 'warning' : 'primary'" size="small">
                    {{ row.groupedSuggestion.kind === 'oldest_first' ? 'OLDEST FIRST' : 'GROUPED' }}
                    {{ row.groupedSuggestion.score }}
                  </vs-chip>
                  <span class="font-bold ml-2">{{ invoiceCount(row.groupedSuggestion.invoices.length) }}</span>
                </div>
                <div class="text-right font-semibold">{{ formatAmount(row.groupedSuggestion.total) }}</div>
              </div>
              <div v-if="row.groupedSuggestion.kind === 'oldest_first'" class="text-xs mt-1 text-warning">
                No combination of open invoices matches this amount: oldest invoices first. Check before allocating.
              </div>
              <ul class="text-xs mt-2">
                <li v-for="inv in row.groupedSuggestion.invoices" :key="inv.invoiceId">
                  {{ inv.invoice_no }} — {{ inv.company_name }}
                  <span v-if="inv.invoice_date">— {{ formatDate(inv.invoice_date) }}</span>
                  — {{ formatAmount(inv.amount) }}
                  <span v-if="parseAmount(inv.credit) > 0">+ {{ formatAmount(inv.credit) }} credit note</span>
                  <span v-if="inv.partial" class="font-semibold">(partial)</span>
                  <span v-if="inv.paid_by_hand" class="text-warning font-semibold">
                    (already marked paid, no payment linked)
                  </span>
                </li>
                <li v-for="cn in (row.groupedSuggestion.credit_notes || [])" :key="'cn' + cn.creditNoteId">
                  {{ cn.invoice_no }} — {{ cn.company_name }}
                  <span v-if="cn.invoice_date">— {{ formatDate(cn.invoice_date) }}</span>
                  — credit note −{{ formatAmount(cn.amount) }} deducted by the client
                </li>
              </ul>
              <div v-if="parseAmount(row.groupedSuggestion.unplaced) > 0.01" class="text-xs mt-1">
                {{ formatAmount(row.groupedSuggestion.unplaced) }} left unplaced: this client has no other open invoice dated before the transfer.
              </div>
              <div class="text-xs text-grey mt-1">
                <span v-for="r in row.groupedSuggestion.reasons" :key="r" class="reason-tag">{{ r }}</span>
              </div>
              <div class="mt-2">
                <vs-button size="small" :color="row.groupedSuggestion.kind === 'oldest_first' ? 'warning' : 'primary'"
                  @click="allocateGrouped(row.transaction, row.groupedSuggestion)">
                  Allocate to {{ invoiceCount(row.groupedSuggestion.invoices.length) }}
                </vs-button>
              </div>
            </div>
          </div>
        </div>
        <vs-divider />
      </div>
    </div>

    <!-- Split modal -->
    <vs-popup title="Split the payment" :active.sync="splitOpen">
      <div v-if="splitContext">
        <p class="mb-2">
          Transaction: <strong>{{ formatAmount(splitContext.transaction.gcl_amount) }}
          {{ splitContext.transaction.gcl_currency || 'EUR' }}</strong>
        </p>
        <table class="w-full mb-4">
          <thead>
            <tr><th>Invoice</th><th>Outstanding</th><th>To allocate</th><th></th></tr>
          </thead>
          <tbody>
            <tr v-for="(line, idx) in splitLines" :key="idx">
              <td>{{ line.invoice_no }} — {{ line.company_name }}</td>
              <td>{{ formatAmount(line.remaining_due) }}</td>
              <td>
                <vs-input type="number" v-model="line.amount" step="0.01" />
              </td>
              <td>
                <vs-button v-if="splitLines.length > 1" size="small" color="danger" type="border"
                  icon="icon-x" icon-pack="feather" @click="removeSplitLine(idx)"></vs-button>
              </td>
            </tr>
          </tbody>
        </table>
        <p :class="splitMismatch ? 'text-danger' : 'text-success'">
          Total: {{ formatAmount(splitTotal) }} /
          {{ formatAmount(splitContext.transaction.gcl_amount) }}
        </p>
        <div class="flex justify-between mt-4">
          <vs-button type="border" @click="addSplitLine">+ Add an invoice</vs-button>
          <vs-button color="success" :disabled="splitMismatch" @click="confirmSplit">Confirm</vs-button>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
export default {
  name: "SmartMatchingTable",
  props: {
    byTransaction: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      splitOpen: false,
      splitContext: null,
      splitLines: [],
    };
  },
  computed: {
    splitTotal() {
      return this.splitLines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
    },
    splitMismatch() {
      if (!this.splitContext) return true;
      const tx = Math.abs(parseFloat(this.splitContext.transaction.gcl_amount));
      return Math.abs(this.splitTotal - tx) > 0.01 ||
             this.splitLines.some(l => !l.idoperation || !(parseFloat(l.amount) > 0));
    },
  },
  methods: {
    formatAmount(v) {
      const n = parseFloat(v);
      if (!isFinite(n)) return "—";
      return n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatDate(d) {
      if (!d) return "—";
      return new Date(d).toLocaleDateString('en-GB');
    },
    parseAmount(v) {
      const n = parseFloat(v);
      return isFinite(n) ? n : 0;
    },
    invoiceCount(n) {
      return n === 1 ? "1 invoice" : `${n} invoices`;
    },
    truncate(s, n) {
      if (!s) return "";
      return s.length > n ? s.slice(0, n) + "…" : s;
    },
    confidenceColor(score) {
      if (score >= 80) return "success";
      if (score >= 40) return "warning";
      return "danger";
    },
    // What this transaction can actually settle on that invoice: never more
    // than the transfer, never more than what the invoice still owes. The
    // second cap was missing, so a transfer larger than the outstanding was
    // posted whole ("Allocate in full") and the excess vanished into the
    // invoice.
    allocationAmount(tx, cand) {
      const txAmount = Math.abs(parseFloat(tx.gcl_amount));
      const remaining = parseFloat(cand.remaining_due);
      if (!isFinite(remaining) || remaining <= 0) return txAmount;
      return Math.min(txAmount, remaining);
    },
    // Label of the allocate button: it must always say what will be posted.
    allocationLabel(tx, cand) {
      const amount = this.allocationAmount(tx, cand);
      const txAmount = Math.abs(parseFloat(tx.gcl_amount));
      const remaining = parseFloat(cand.remaining_due);
      if (Math.abs(amount - remaining) <= 0.01 && txAmount - remaining > 0.01) {
        // The transfer covers this invoice and still has money left over.
        return `Allocate ${this.formatAmount(amount)} (settles it, ${this.formatAmount(txAmount - amount)} left)`;
      }
      if (txAmount - amount > 0.01 || cand.partial) {
        return `Allocate ${this.formatAmount(amount)} (partial)`;
      }
      return "Allocate in full";
    },
    allocateFull(tx, cand) {
      const amount = this.allocationAmount(tx, cand);
      this.$emit("allocate", {
        idtransaction: tx.id,
        allocations: [{ idoperation: cand.invoiceId, amount, type: 'bank_allocation' }],
      });
    },
    // Invoices fully covered by a credit note carry no money from the transfer:
    // only the credit note offset is posted on them.
    allocateGrouped(tx, grouped) {
      this.$emit("allocate", {
        idtransaction: tx.id,
        allocations: grouped.invoices
          .filter(inv => parseFloat(inv.amount) > 0)
          .map(inv => ({
            idoperation: inv.invoiceId,
            amount: inv.amount,
            type: 'bank_allocation',
          })),
        credit_notes: (grouped.credit_notes || []).flatMap(cn => cn.applied.map(a => ({
          idcreditnote: cn.creditNoteId,
          idoperation: a.invoiceId,
          amount: a.amount,
        }))),
      });
    },
    openSplit(tx, cand) {
      this.splitContext = { transaction: tx, seed: cand };
      this.splitLines = [{
        idoperation: cand.invoiceId,
        invoice_no: cand.invoice_no,
        company_name: cand.company_name,
        remaining_due: cand.remaining_due,
        amount: Math.min(parseFloat(tx.gcl_amount), parseFloat(cand.remaining_due)).toFixed(2),
      }];
      this.splitOpen = true;
    },
    addSplitLine() {
      this.splitLines.push({
        idoperation: null, invoice_no: '', company_name: '', remaining_due: 0, amount: 0,
      });
    },
    removeSplitLine(idx) {
      this.splitLines.splice(idx, 1);
    },
    confirmSplit() {
      this.$emit("allocate", {
        idtransaction: this.splitContext.transaction.id,
        allocations: this.splitLines
          .filter(l => l.idoperation && parseFloat(l.amount) > 0)
          .map(l => ({
            idoperation: l.idoperation,
            amount: parseFloat(l.amount),
            type: 'bank_allocation',
          })),
      });
      this.splitOpen = false;
    },
  },
};
</script>

<style scoped>
.match-row { border-left: 3px solid #e0e0e0; padding-left: 12px; }
.tx-cell { background: #f7f9fb; padding: 12px; border-radius: 4px; }
.cand-card { background: #ffffff; border: 1px solid #e0e0e0; border-radius: 4px;
  padding: 10px; margin-bottom: 8px; }
.cand-card.grouped { border-color: #7367f0; background: #f7f6ff; }
.cand-card.grouped.oldest-first { border-color: #ff9f43; background: #fff8f1; }
.text-warning { color: #ff9f43; }
.reason-tag { display: inline-block; background: #eef1f6; padding: 2px 6px;
  border-radius: 3px; margin-right: 4px; font-size: 10px; }
.text-grey { color: #888; }
.text-danger { color: #ea5455; }
.text-success { color: #28c76f; }
</style>
